// Die Regeln des Modell-Pickers (model-picker.tsx) — rein, getestet.

import type { GatewayModel } from "../../api/types";
import { fmtInt } from "../../lib/format";

/** Rechenzentren in der EU/Europa — für den Filter „nur EU". */
export const EU_REGION = /@(?:[a-z-]*(?:westeurope|northeurope|germany|france|sweden|switzerland|italy|spain|poland|norway|europe)[a-z0-9-]*|eu-[a-z0-9-]+)$/i;

/** Mehr Zeilen zeichnet die Liste nicht — darüber hilft nur die Suche. */
export const MAX_ROWS = 300;

export interface PickerRow extends Partial<GatewayModel> {
  id: string;
  /** Ausgewählt, aber nicht (mehr) im Katalog des Gateways. */
  missing?: boolean;
}

export interface PickerFilter {
  q: string;
  eu: boolean;
  only: boolean;
}

/**
 * Die Zeilen der Liste: Katalog plus ausgewählte, die das Gateway nicht
 * (mehr) führt — sonst ließen sie sich nicht abwählen —, gefiltert, die
 * ausgewählten zuerst, dann nach Kennung. `more` = was über 300 hinausgeht.
 */
export function pickerRows(catalog: readonly GatewayModel[], selected: readonly string[], filter: PickerFilter): { rows: PickerRow[]; total: number; more: number; priced: boolean } {
  const q = filter.q.trim().toLowerCase();
  const ids = new Set(selected);
  const known = new Set(catalog.map((m) => m.id));
  const all: PickerRow[] = [...catalog, ...[...ids].filter((id) => !known.has(id)).map((id) => ({ id, missing: true }))];
  const rows = all
    .filter((m) => (!q || m.id.toLowerCase().includes(q)) && (!filter.eu || EU_REGION.test(m.id)) && (!filter.only || ids.has(m.id)))
    .sort((a, b) => Number(ids.has(b.id)) - Number(ids.has(a.id)) || a.id.localeCompare(b.id));
  return { rows: rows.slice(0, MAX_ROWS), total: rows.length, more: Math.max(0, rows.length - MAX_ROWS), priced: catalog.some((m) => m.input !== null) };
}

/** Eine Auswahl umschalten: eindeutig und sortiert, wie die Liste gespeichert wird. */
export function toggleModel(selected: readonly string[], id: string, on: boolean): string[] {
  const cur = new Set(selected);
  if (on) cur.add(id); else cur.delete(id);
  return [...cur].sort();
}

/** „3 Modelle" / „alle Modelle des Gateways" (leere Liste = keine Einschränkung). */
export function countText(selected: readonly string[]): string {
  return selected.length ? `${fmtInt(selected.length)} ${selected.length === 1 ? "Modell" : "Modelle"}` : "alle Modelle des Gateways";
}

/** Kontextfenster: „1,05 Mio." statt „1.050k". */
export const ctxText = (n: number): string =>
  (n >= 1e6 ? `${(n / 1e6).toLocaleString("de-DE", { maximumFractionDigits: 2 })} Mio.` : `${fmtInt(Math.round(n / 1000))}k`);

export const money = (v: number | null | undefined): string =>
  (v === null || v === undefined ? "—" : v < 1 ? v.toFixed(2) : v.toFixed(v < 10 ? 2 : 1));

/** Der Satz zu einer Antwort des Katalogs, die keine Liste ist. */
export function catalogErrorText(error: string, detail?: string): string {
  if (error === "base_url_missing") return "Erst die Adresse des Gateways eintragen und speichern.";
  if (error === "platform_keys_off") return "Plattform-Keys sind aus (REQUESTY_MANAGEMENT_KEY fehlt).";
  return `Katalog nicht abrufbar: ${detail ?? error}`;
}
